<template>
  <BaseWidget
    title="Completed tours"
    :show-info-icon="true"
    info-tooltip="Tours that have ended within the selected date range"
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/4088442-how-do-i-enable-leads-to-book-tours-from-my-website', text: 'Learn about Tours' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    custom-class="completed-tours-widget"
    :date-range="dateRange"
    :past-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading && !data"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="true"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <template v-else>
      <div
        ref="widgetRef"
        class="widget-content-wrapper"
        :class="{ 'focused': focused }"
      >
        <div class="tour-count">
          <div class="count-row">
            <span class="count">{{ tourCount }}</span>
            <ComparisonChip
              :change="changePercentage"
              :previous-value="formattedPreviousCount"
              :previous-period-dates="previousTimestamps"
              :date-range-preset="dateRange"
              label="Completed tours"
            />
          </div>
          <div class="count-label-row">
            <span class="count-label">{{ dateRangeLabel }}</span>
            <a
              v-if="tourCount > 3"
              class="focused-view-all-link"
              @click.prevent="handleViewAllClick"
            >
              View all
            </a>
          </div>
        </div>

        <div
          v-if="!focused"
          class="tours-list"
        >
          <div
            v-for="(tour, index) in displayTours"
            :key="tour.id || index"
            class="tour-item"
            @click="handleTourClickGuarded(tour)"
          >
            <div class="tour-row">
              <div class="tour-image">
                <img
                  v-if="tour.image"
                  :src="tour.image"
                  :alt="tour.name"
                >
                <div
                  v-else
                  class="image-placeholder"
                >
                  {{ getInitials(tour.name) }}
                </div>
              </div>
              <div class="tour-details">
                <div class="tour-name">
                  {{ tour.name }}
                </div>
                <div class="tour-meta">
                  <span class="tour-time">{{ formatTourTime(tour) }}</span>
                  <span
                    v-if="tour.user?.name"
                    class="tour-meta-sep"
                  > • </span>
                  <span
                    v-if="tour.user?.name"
                    class="tour-account-name"
                  >{{ tour.user.name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="displayTours.length === 0 && tours.length === 0"
            class="no-tours"
          >
            No completed tours
          </div>
        </div>
      </div>
    </template>
  </BaseWidget>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useGraphQLData } from '../composables/useWidgetData.js'
import { getDemoCompletedToursData } from '../services/demoData.js'
import { DATE_RANGE_LABELS, getDateRangeTimestamps, getPreviousPeriodTimestamps, getDateRangeDates } from '../utils/dateRanges.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { useLocationFilter } from '../composables/useLocationFilter.js'
import { useReferenceDate } from '../composables/useReferenceDate.js'
import { executeGraphQLQuery } from '../services/optixApi.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { getResourceImageUrl } from '../utils/resourceImage.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  },
  rowSpan: {
    type: Number,
    default: 2
  },
  colSpan: {
    type: Number,
    default: 1
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

const dateRange = ref(props.props?.dateRange || 'last_7_days')
const widgetRef = ref(null)

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Last 7 days'
})

const locationIds = useLocationFilter()
const referenceDate = useReferenceDate()

const timestamps = computed(() => {
  return getDateRangeTimestamps(dateRange.value, referenceDate.value)
})

const previousTimestamps = computed(() => {
  return getPreviousPeriodTimestamps(dateRange.value, referenceDate.value)
})

// Query completed tours with full details for display, limited to 3 items
const completedToursQuery = `
  query ScheduleNavigationDebounced($location_id: [ID!], $from_timestamp: Int!, $to_timestamp: Int!, $limit: Int, $page: Int) {
    schedule(
      location_id: $location_id
      start_timestamp_to: $to_timestamp
      end_timestamp_from: $from_timestamp
      limit: $limit
      page: $page
      type: TOUR
      status: ENDED
    ) {
      total
      data {
        tour_id
        title
        start_timestamp
        end_timestamp
        resource {
          resource_id
          name
          main_image {
            url
            __typename
          }
          location {
            location_id
            name
            timezone
            timezone_name
            __typename
          }
          __typename
        }
        location {
          location_id
          name
          timezone
          timezone_name
          __typename
        }
        owner_account {
          account_id
          name
          avatar {
            url
            __typename
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`

const previousQuery = `
  query ScheduleNavigationDebounced($location_id: [ID!], $from_timestamp: Int!, $to_timestamp: Int!, $limit: Int, $page: Int) {
    schedule(
      location_id: $location_id
      start_timestamp_to: $to_timestamp
      end_timestamp_from: $from_timestamp
      limit: $limit
      page: $page
      type: TOUR
      status: ENDED
    ) {
      total
      __typename
    }
  }
`

const { data, loading, error, cleanup, refresh } = useGraphQLData({
  dataKey: 'completed_tours',
  query: completedToursQuery,
  variables: computed(() => ({
    from_timestamp: timestamps.value.from_timestamp,
    to_timestamp: timestamps.value.to_timestamp,
    limit: 3,
    page: 1,
    location_id: locationIds.value.length > 0 ? locationIds.value : undefined
  })),
  priority: 2,
  autoFetch: true,
  getDemoData: () => getDemoCompletedToursData({
    from_timestamp: timestamps.value.from_timestamp,
    to_timestamp: timestamps.value.to_timestamp
  })
})

const {
  data: previousData,
  loading: previousLoading,
  cleanup: previousCleanup,
  refresh: refreshPrevious
} = useGraphQLData({
  dataKey: 'completed_tours_previous',
  query: previousQuery,
  variables: computed(() => ({
    from_timestamp: previousTimestamps.value.from_timestamp,
    to_timestamp: previousTimestamps.value.to_timestamp,
    limit: 1,
    page: 1,
    location_id: locationIds.value.length > 0 ? locationIds.value : undefined
  })),
  priority: 2,
  autoFetch: true,
  getDemoData: () => getDemoCompletedToursData({
    from_timestamp: previousTimestamps.value.from_timestamp,
    to_timestamp: previousTimestamps.value.to_timestamp
  })
})

// Ensure previous data is fetched when current data loads
watch(() => data.value, (newData) => {
  if (newData && !previousLoading.value && !previousData.value) {
    refreshPrevious(true)
  }
}, { immediate: true })

// Map schedule data to display format
const tours = computed(() => {
  if (!data.value?.schedule?.data) {
    return []
  }
  return data.value.schedule.data
    .map(item => {
      const location = item.location || item.resource?.location
      const timezone = location?.timezone || null

      return {
        id: item.tour_id,
        name: item.title || item.resource?.name || item.location?.name || 'Unnamed Tour',
        time: item.start_timestamp * 1000,
        endTime: item.end_timestamp * 1000,
        image: getResourceImageUrl(null, item.resource?.name || item.title || 'Tour'),
        location: location?.name || null,
        timezone: timezone,
        user: item.owner_account ? {
          name: item.owner_account.name || null,
          avatar: getAvatarUrl(item.owner_account.avatar?.url, item.owner_account.name || item.owner_account.account_id)
        } : null
      }
    })
    .sort((a, b) => b.time - a.time) // Sort by most recent first
})

const tourCount = computed(() => {
  return data.value?.schedule?.total ?? tours.value.length
})

const previousCount = computed(() => {
  if (previousData.value === null || previousData.value === undefined) return null
  return previousData.value?.schedule?.total ?? 0
})

const changePercentage = computed(() => {
  if (loading.value || previousLoading.value) return null
  if (previousData.value === null || previousData.value === undefined) return null

  const current = tourCount.value
  const previous = previousCount.value

  if (previous === 0 && current === 0) return null
  if (previous === 0 && current > 0) return 100

  const change = ((current - previous) / previous) * 100
  return Math.round(change * 10) / 10
})

const formattedPreviousCount = computed(() => {
  if (previousCount.value === null || previousCount.value === undefined) return null
  return previousCount.value.toString()
})

// Display up to 3 tours
const displayTours = computed(() => {
  return tours.value.slice(0, 3)
})

// Format tour time for display using the location's timezone
const formatTourTime = (tour) => {
  if (!tour.time) return ''

  const timezone = (tour.timezone && typeof tour.timezone === 'string' && tour.timezone.trim()) || undefined
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone
  })

  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: timezone
  })

  // Check if timezone differs from user's
  let showTimezoneIndicator = false
  let timezoneFormatter = null
  if (timezone) {
    try {
      const locParts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'shortOffset' }).formatToParts(new Date(tour.time))
      const userParts = new Intl.DateTimeFormat('en-US', { timeZone: userTimezone, timeZoneName: 'shortOffset' }).formatToParts(new Date(tour.time))
      const locOffset = locParts.find(p => p.type === 'timeZoneName')?.value || ''
      const userOffset = userParts.find(p => p.type === 'timeZoneName')?.value || ''
      showTimezoneIndicator = locOffset !== userOffset
      if (showTimezoneIndicator) {
        timezoneFormatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
      }
    } catch (_) {
      showTimezoneIndicator = timezone !== userTimezone
      if (showTimezoneIndicator) {
        timezoneFormatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
      }
    }
  }

  const tourDate = new Date(tour.time)
  let timeStr = timeFormatter.format(tourDate)

  if (showTimezoneIndicator && timezoneFormatter) {
    const timezoneParts = timezoneFormatter.formatToParts(tourDate)
    const timezoneName = timezoneParts.find(p => p.type === 'timeZoneName')?.value || ''
    if (timezoneName) {
      timeStr = `${timeStr} (${timezoneName})`
    }
  }

  const dateStr = dateFormatter.format(tourDate)
  return `${dateStr} @ ${timeStr}`
}

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return {
    client_id: urlParams.get('client_id') || urlParams.get('clientId') || '',
    canvasUUID: urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || '',
  }
}

// Handle "View all" click - navigate to schedule list page via parent window
const handleViewAllClick = () => {
  const params = getUrlParams()
  let clientId = params.client_id
  let canvasUUID = params.canvasUUID

  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer)
      const referrerParams = new URLSearchParams(referrerUrl.search)
      if (!clientId) {
        clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      }
      if (!canvasUUID) {
        canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
      }
    } catch (e) {
      // ignore referrer parse errors
    }
  }

  if (!clientId || !canvasUUID) {
    try {
      if (window.parent && window.parent !== window) {
        const parentUrl = window.parent.location.href
        const parentUrlObj = new URL(parentUrl)
        const parentParams = new URLSearchParams(parentUrlObj.search)
        if (!clientId) {
          clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
        }
        if (!canvasUUID) {
          canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
        }
      }
    } catch (e) {
      // CORS - can't access parent location
    }
  }

  const finalCanvasUUID = canvasUUID || "default-canvas-uuid"
  const dates = getDateRangeDates(dateRange.value)

  const message = {
    command: "ReplaceUrl",
    payload: {
      client_id: clientId || "",
      canvasUUID: finalCanvasUUID,
      url: `/schedule/list?event_types=TOUR&date_from=${dates.from}&date_to=${dates.to}`,
      reload: true
    }
  }

  try {
    window.parent.postMessage(message, "*")
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, "*")
    }
  } catch (error) {
    console.error('Error posting message:', error)
  }
}

// GraphQL query to fetch tour web_link
const TOUR_QUERY = `
  query GetTourWebLink($tour_id: ID!) {
    tour(tour_id: $tour_id) {
      web_link
    }
  }
`

const { guard } = useTrialModeGuard()

const handleTourClickGuarded = guard(async (tour) => {
  if (!tour.id) {
    console.error('Tour ID not found:', tour)
    return
  }

  try {
    const result = await executeGraphQLQuery(
      TOUR_QUERY,
      { tour_id: tour.id },
      'GetTourWebLink'
    )

    const data = result.data?.data || result.data

    if (data?.tour?.web_link) {
      window.open(data.tour.web_link, '_blank')
    } else {
      console.error('Tour web_link not found in response:', data)
    }
  } catch (error) {
    console.error('Error fetching tour web_link:', error)
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

watch(dateRange, () => {
  refresh(true)
})

onUnmounted(() => {
  cleanup()
  previousCleanup()
})
</script>

<style scoped>
:deep(.completed-tours-widget) {
  cursor: pointer;
  overflow: hidden;
}

:deep(.completed-tours-widget .widget-content) {
  justify-content: flex-start;
  overflow: hidden;
}

.tour-count {
  display: flex;
  flex-direction: column;
  margin-bottom: 2px;
}

.count-row {
  display: flex;
  align-items: baseline;
  margin-bottom: 6px;
}

.count {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
  margin-top: -1px;
}

.count-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 2px;
  padding-bottom: 14px;
}

.count-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.widget-content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.widget-content-wrapper.focused {
  position: relative;
}

.tours-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  overflow: visible;
  min-height: 0;
  max-height: none;
}

.tour-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
  margin: 0 -4px;
  padding: 10px 4px;
  min-height: 64px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

.tour-item:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.tour-item:last-child {
  border-bottom: none;
}

.tour-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.tour-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.tour-image {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.tour-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.tour-details {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.tour-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 4px;
}

.tour-meta {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 2px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tour-meta-sep {
  color: rgba(0, 0, 0, 0.87);
  margin: 0 4px;
}

.tour-account-name {
  color: rgba(0, 0, 0, 0.87);
}

.tour-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.no-tours {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.tours-widget-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 32px;
  flex-shrink: 0;
  padding-top: 4px;
}

.view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  transition: color 0.2s ease;
}

.view-all-link:hover {
  color: #2563eb;
}

.focused-view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
  flex-shrink: 0;
}

.focused-view-all-link:hover {
  color: #2563eb;
}
</style>
